import {
    ColDef,
    Column,
    ColumnResizedEvent,
    ColumnRowGroupChangedEvent,
    ColumnState,
} from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import {
    DataGridComponent,
    OverridesRelayService,
    OverrideVizType,
    UserGridColumnOverrides,
    UserGridColumnOverridesMap,
    UserVisualizationOverridesForDashboard,
} from '@ddv/data-grid';
import { FeatureFlagService } from '@ddv/entitlements';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { ManagerService } from '@ddv/layout';
import {
    AgGridFilterModel,
    AgGridSortDirection,
    AUTO_GROUP_COLUMN_ID,
    crosstalkCheckboxFieldId,
    CustomColumnState,
    GridConfigItem,
    EDITABLE_CHECKBOX_FIELD,
    getCurrentVisualizationId,
    VisualizationType,
} from '@ddv/models';
import { deepCompare } from '@ddv/utils';
import {
    combineLatest,
    debounceTime,
    first,
    map,
    Observable,
    of,
    ReplaySubject,
    share,
    switchMap,
} from 'rxjs';

interface UserGridColumnOverridesObservable {
    url: string;
    method: string;
    body: UserGridColumnOverridesMap | null;
}

// this service does not belong down here in lib-visualizations
// the data it updates hangs off of the widget-on-board objects, but because this code
// is so tightly coupled with the monsterously massive *-grid-config services
// we don't have any choice but to leave it for now
@Injectable({ providedIn: 'root' })
export class UserGridColumnOverridesService {
    private readonly currentStateService = inject(CurrentStateService);
    private readonly ddvMwService = inject<ApiExecutorService>(ApiServices.ddvMW);
    private readonly ddvApiService = inject<ApiExecutorService>(ApiServices.ddvApi);
    private readonly manager = inject(ManagerService);
    private readonly relayService = inject(OverridesRelayService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly updateGridColumnOverridesSubject = new ReplaySubject<UserGridColumnOverridesObservable>(1);
    private updateGridColumnOverrides$: Observable<UserGridColumnOverridesObservable | unknown> | undefined;
    private checkboxColumnDefinition: ColDef | undefined;
    private readonly excludedColumns: string[] = [
        EDITABLE_CHECKBOX_FIELD,
        AUTO_GROUP_COLUMN_ID,
    ];
    private clientCode = '';
    private overrides: UserVisualizationOverridesForDashboard = {};

    constructor() {
        this.relayService.link(this);

        this.manager.currentDashboardId.pipe(
            switchMap((dashboardId) => {
                return combineLatest([this.currentStateService.clientCode$, of(dashboardId)]);
            }),
            switchMap(([clientCode, dashboardId]: [string, string | number]) => {
                this.clientCode = clientCode;
                return combineLatest([
                    this.featureFlagService.isFlagEnabled('use-aws-for-visualization-overrides', clientCode),
                    of(dashboardId),
                ]);
            }),
            switchMap(([isFlagEnabled, dashboardId]: [boolean, string | number]) => {
                if (isFlagEnabled) {
                    return this.ddvApiService.invokeService<UserVisualizationOverridesForDashboard>(
                        this.clientCode,
                        `user/dashboards/${dashboardId}/visualization-overrides`,
                        'GET',
                    );
                }
                this.overrides = {};
                this.updateGridColumnOverrides$ = this.updateGridColumnOverridesSubject.pipe(
                    debounceTime(150),
                    switchMap(({ url, method, body }) => {
                        return this.ddvMwService.invokeServiceWithBody(this.clientCode, url, method, body);
                    }),
                    share());
                return of({} as UserVisualizationOverridesForDashboard);
            }))
            .subscribe((overrides) => {
                this.overrides = overrides;
            });
    }

    getCurrentGridColumnOverrides(
        widgetOnBoardId: number,
        visualizationId: number,
        vizType?: VisualizationType,
    ): UserGridColumnOverrides[] {
        const viz = mapVisualizationTypeToOverrideVizType(vizType);
        const overrides = this.getGridColumnOverrides(widgetOnBoardId);
        return this.overrides?.[widgetOnBoardId]?.[viz] ?? overrides[visualizationId] ?? [];
    }

    setCheckboxColumnDefinition(checkboxColumnDefinition: ColDef): void {
        this.checkboxColumnDefinition = checkboxColumnDefinition;
    }

    getColumnsOrder(
        userGridColumnOverrides: UserGridColumnOverrides[],
        isInViewMode = false,
        columns: GridConfigItem[],
        initialColumnState: CustomColumnState[],
    ): GridConfigItem[] {
        const columnsOrder: UserGridColumnOverrides[] = isInViewMode ?
            userGridColumnOverrides.sort((a, b) => a.columnPosition! > b.columnPosition! ? 1 : -1) :
            initialColumnState.map((c, index) => ({ columnId: c.colId, columnPosition: index }));

        const updatedColumns = columns;
        columnsOrder.forEach((override) => {
            if (override?.columnPosition != null) {
                const columnIndex = updatedColumns.findIndex((column) => column.colId === override.columnId);
                if (columnIndex !== -1) {
                    updatedColumns.splice(override.columnPosition, 0, columns.splice(columnIndex, 1)[0]);
                }
            }
        });
        return updatedColumns;
    }

    setColumnsSort(
        userGridColumnOverrides: UserGridColumnOverrides[],
        isInViewMode = false,
        columns: ColumnState[],
        initialColumnState: CustomColumnState[],
        dataGridComponent: DataGridComponent,
    ): void {
        const sortOrder: UserGridColumnOverrides[] = isInViewMode ?
            userGridColumnOverrides :
            initialColumnState.map((c) => ({
                columnId: c.colId,
                agSort: c.sort ?? null,
                agSortIndex: (c.sortIndex !== undefined ? c.sortIndex : undefined) ?? undefined,
            }));

        const sortState: ColumnState[] = [];
        columns.forEach((column) => {
            const { agSort, agSortIndex } = this.getColumnSort(sortOrder, initialColumnState, column);

            sortState.push({
                colId: column.colId,
                sort: agSort.sort,
                sortIndex: agSortIndex,
            });
        });

        dataGridComponent.applyColumnState({ state: sortState, applyOrder: false });
    }

    setColumnsFilterModel(
        overrides: UserGridColumnOverrides[],
        isInViewMode = false,
        initialColumnState: CustomColumnState[],
        dataGridComponent: DataGridComponent,
    ): void {
        const filterModel = this.getColumnsFilterModel(overrides, isInViewMode, initialColumnState);

        if (dataGridComponent && !deepCompare(dataGridComponent.getFilterModel(), filterModel)) {
            // Calling setFilterModel with no arguments helps us set the correct filters
            // in case we deselect all options from a set (string) filter in view mode and then:
            // - switch to edit mode
            // - hit 'Restore Default View' button

            setTimeout(() => {
                dataGridComponent.setFilterModel();
                dataGridComponent.setFilterModel(filterModel);
            }, 100);
        }
    }

    updateColumnsOrder(widgetOnBoardId: number, columnIds: string[]): void {
        const widgetPrefs = this.manager.getWidgetPreferences(widgetOnBoardId);
        if (widgetPrefs?.isDetailWidget) {
            return;
        }

        if (columnIds.length) {
            const visualizationId = getCurrentVisualizationId(widgetPrefs)!;
            const userGridColumnOverrides = this.getCurrentGridColumnOverrides(
                widgetOnBoardId,
                visualizationId,
                widgetPrefs?.currentVisualization,
            );
            columnIds.forEach((columnId: string, index: number) => {
                const column = userGridColumnOverrides.find((override) => override.columnId === columnId);
                if (column) {
                    column.columnPosition = index;
                } else {
                    const newItem: UserGridColumnOverrides = { columnId, columnPosition: index };
                    userGridColumnOverrides.push(newItem);
                }
            });

            const newColumnOrder = userGridColumnOverrides
                .filter((column) => this.excludedColumns.indexOf(column.columnId) === -1)
                .map((column) => ({ columnId: column.columnId, columnPosition: column.columnPosition }));

            if (newColumnOrder.length) {
                const updatedOverrides = { [visualizationId]: newColumnOrder };
                this.updateGridColumnOverrides(widgetOnBoardId, 'columnOrder', updatedOverrides, widgetPrefs?.currentVisualization)
                    .pipe(first())
                    .subscribe();
            }
        }
    }

    updateColumnVisibility(widgetOnBoardId: number, columns: { [colId: string]: boolean }): void {
        const widgetPrefs = this.manager.getWidgetPreferences(widgetOnBoardId);
        if (widgetPrefs?.isDetailWidget) {
            return;
        }

        const visualizationId = getCurrentVisualizationId(widgetPrefs)!;
        const userGridColumnOverrides = this.getCurrentGridColumnOverrides(
            widgetOnBoardId,
            visualizationId,
            widgetPrefs?.currentVisualization,
        );
        Object.keys(columns).forEach((key) => {
            const column = userGridColumnOverrides.find((c) => c.columnId === key);
            if (column) {
                column.isHidden = !columns[key];
            } else {
                const newItem: UserGridColumnOverrides = { columnId: key, isHidden: !columns[key] };
                userGridColumnOverrides.push(newItem);
            }
        });
        const newColumnVisibility = userGridColumnOverrides.map((c) => ({
            columnId: c.columnId,
            isHidden: c.isHidden,
        }));

        const updatedOverrides = { [visualizationId]: newColumnVisibility };
        this.updateGridColumnOverrides(widgetOnBoardId, 'columnVisibility', updatedOverrides, widgetPrefs?.currentVisualization)
            .pipe(first())
            .subscribe();
    }

    updateColumnSort(widgetOnBoardId: number, columnState: ColumnState[]): void {
        const widgetPrefs = this.manager.getWidgetPreferences(widgetOnBoardId);
        if (widgetPrefs?.isDetailWidget) {
            return;
        }

        const visualizationId = getCurrentVisualizationId(widgetPrefs)!;
        const userGridColumnOverrides = this.getCurrentGridColumnOverrides(
            widgetOnBoardId,
            visualizationId,
            widgetPrefs?.currentVisualization,
        );
        const columnSortState = columnState.map((column) => ({
            columnId: column.colId,
            sort: column.sort,
            sortIndex: column.sortIndex,
        }));

        columnSortState.forEach((column) => {
            const override = userGridColumnOverrides.find((col) => col.columnId === column.columnId);

            if (column.columnId !== crosstalkCheckboxFieldId) {
                if (override) {
                    override.agSort = column.sort ?? 'none';
                    override.agSortIndex = column.sortIndex ?? undefined;
                } else {
                    userGridColumnOverrides.push({
                        columnId: column.columnId,
                        agSort: column.sort ?? 'none',
                        agSortIndex: column.sortIndex ?? undefined,
                    });
                }
            }
        });

        const newColumnSort = userGridColumnOverrides.map((column) => ({
            columnId: column.columnId,
            agSort: Object.is(column.agSort, null) ? 'none' : column.agSort,
            agSortIndex: column.agSortIndex,
        }));

        if (newColumnSort.length) {
            const updatedOverrides = { [visualizationId]: newColumnSort };
            this.updateGridColumnOverrides(widgetOnBoardId, 'columnSort', updatedOverrides, widgetPrefs?.currentVisualization)
                .pipe(first())
                .subscribe();
        }
    }

    updateColumnFilter(
        widgetOnBoardId: number,
        appliedFilters: AgGridFilterModel,
        columns: Column[],
        initialColumnState: CustomColumnState[],
        isClearAll = false,
    ): void {
        const widgetPrefs = this.manager.getWidgetPreferences(widgetOnBoardId);
        if (widgetPrefs?.isDetailWidget) {
            return;
        }

        const visualizationId = getCurrentVisualizationId(widgetPrefs)!;
        const userGridColumnOverrides = this.getCurrentGridColumnOverrides(
            widgetOnBoardId,
            visualizationId,
            widgetPrefs?.currentVisualization,
        );
        const appliedFiltersKeys = Object.keys(appliedFilters);

        if (!appliedFiltersKeys.length) {
            if (userGridColumnOverrides.length) {
                userGridColumnOverrides.forEach((column) => {
                    const isChanged = initialColumnState.some((c) => c.colId === column.columnId && c.agFilter);
                    column.agFilter = column.agFilter === 'default' && !isClearAll && !isChanged ? 'default' : null;
                    column.agFilterIndex = null;
                });
            } else {
                columns.forEach((column) => {
                    userGridColumnOverrides.push({
                        columnId: column.getColId(),
                        agFilter: null,
                        agFilterIndex: null,
                    });
                });
            }
        } else {
            const currentColumnsFilters: { columnId: string, filtersData?: string }[] = [];
            appliedFiltersKeys.forEach((key) => {
                const columnFilter = currentColumnsFilters.find((c) => c.columnId === key);
                if (columnFilter) {
                    columnFilter.filtersData = appliedFilters[key];
                } else {
                    currentColumnsFilters.push({
                        columnId: key,
                        filtersData: appliedFilters[key],
                    });
                }
            });

            initialColumnState.forEach((column) => {
                if (column.agFilter && !currentColumnsFilters.find((col) => col.columnId === column.colId)) {
                    currentColumnsFilters.push({
                        columnId: column.colId,
                        filtersData: undefined,
                    });
                }
            });

            currentColumnsFilters.forEach((filter, index) => {
                const appliedFilterIndex = appliedFiltersKeys.findIndex((key) => filter.columnId === key);

                const override = userGridColumnOverrides.find((c) => c.columnId === filter.columnId);
                const filterToApply: UserGridColumnOverrides = {
                    columnId: override ? override.columnId : filter.columnId,
                    agFilter: appliedFilterIndex !== -1 ?
                        filter.filtersData === 'default' || filter.filtersData === undefined ?
                            'default' :
                            JSON.stringify(filter.filtersData) :
                        null,
                    agFilterIndex: appliedFilterIndex !== -1 ? index : null,
                };

                if (!override) {
                    userGridColumnOverrides.push(filterToApply);
                } else {
                    Object.assign(override, filterToApply);
                }
            });

            userGridColumnOverrides.forEach((override) => {
                const isFilterApplied = appliedFiltersKeys.indexOf(override.columnId) !== -1;
                if (!isFilterApplied) {
                    const isChanged = initialColumnState.some((c) => c.colId === override.columnId && c.agFilter);
                    override.agFilter = (override.agFilter === 'default' || override.agFilter === undefined) && !isChanged ?
                        'default' :
                        null;
                    override.agFilterIndex = null;
                }
            });
        }
        const newColumnFilter = userGridColumnOverrides.map((column) => ({
            columnId: column.columnId,
            agFilter: column.agFilter,
            agFilterIndex: column.agFilterIndex,
        }));

        if (newColumnFilter.length) {
            const updatedOverrides = { [visualizationId]: newColumnFilter };
            this.updateGridColumnOverrides(widgetOnBoardId, 'columnFilter', updatedOverrides, widgetPrefs?.currentVisualization)
                .pipe(first())
                .subscribe();
        }
    }

    updateColumnWidth(widgetOnBoardId: number, event: ColumnResizedEvent): void {
        const widgetPrefs = this.manager.getWidgetPreferences(widgetOnBoardId);
        const visualizationId = getCurrentVisualizationId(widgetPrefs)!;
        const overrides = this.getCurrentGridColumnOverrides(
            widgetOnBoardId,
            visualizationId,
            widgetPrefs?.currentVisualization,
        );
        const columnId = event.column?.getColId() ?? '';
        const width = event.column?.getActualWidth();

        const displayedColumns = event.api.getAllDisplayedColumns();
        const widthOverrides = displayedColumns.map((c) => {
            const colId = c.getColId();
            const columnOverride = overrides.find((o) => o.columnId === c.getColId());
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            const widthOverride = columnOverride?.columnWidth != null ? columnOverride.columnWidth : undefined;
            const newColumnWidth = colId === columnId ? width : widthOverride;
            const groupColumnsOrderForGroupWidth = colId === AUTO_GROUP_COLUMN_ID ?
                event.api.getRowGroupColumns().map((gc) => gc.getColId()).join() :
                undefined;
            const newWidthOverride = { columnId: colId, columnWidth: newColumnWidth, groupColumnsOrderForGroupWidth };

            if (!columnOverride) {
                overrides.push(newWidthOverride);
            } else {
                columnOverride.columnWidth = newColumnWidth;
                columnOverride.groupColumnsOrderForGroupWidth = groupColumnsOrderForGroupWidth;
            }

            return newWidthOverride;
        });

        const updatedOverrides = { [visualizationId]: widthOverrides };
        this.updateGridColumnOverrides(widgetOnBoardId, 'columnWidth', updatedOverrides, widgetPrefs?.currentVisualization)
            .pipe(first())
            .subscribe();
    }

    updateGroupColumn(widgetOnBoardId: number, event: ColumnRowGroupChangedEvent): void {
        const widgetPrefs = this.manager.getWidgetPreferences(widgetOnBoardId);
        const visualizationId = getCurrentVisualizationId(widgetPrefs)!;
        const overrides = this.getCurrentGridColumnOverrides(
            widgetOnBoardId,
            visualizationId,
            widgetPrefs?.currentVisualization,
        );
        const groupColumnsOrder = event.api.getRowGroupColumns().map((c) => c.getColId()).join() ?? '';

        // once we fix how the checkbox column position is set
        // we will no longer need to filter it out when sending the other columns to be overrided
        const displayedColumns = event.api.getAllDisplayedColumns().filter((c) => c.getColId() !== crosstalkCheckboxFieldId);

        const groupColumnsOrderOverrides = displayedColumns.map((c) => {
            const colId = c.getColId();
            const newGroupColumnsOrder = colId === AUTO_GROUP_COLUMN_ID ? groupColumnsOrder : undefined;
            const override = overrides.find((o) => o.columnId === colId);
            const newGroupColumnsOrderOverride = { columnId: colId, groupColumnsOrder: newGroupColumnsOrder };

            if (!override) {
                overrides.push(newGroupColumnsOrderOverride);
            } else {
                override.groupColumnsOrder = newGroupColumnsOrder;
            }

            return newGroupColumnsOrderOverride;
        });

        // When the user removes all the groups we don't have the group column in the displayedColumns
        // so we have to explicitly add the override for it
        if (!displayedColumns.find((c) => c.getColId() === AUTO_GROUP_COLUMN_ID) && groupColumnsOrder === '') {
            const groupOverride = overrides.find((o) => o.columnId === AUTO_GROUP_COLUMN_ID);
            const newGroupColumnsOrderOverride = { columnId: AUTO_GROUP_COLUMN_ID, groupColumnsOrder };
            if (!groupOverride) {
                overrides.push(newGroupColumnsOrderOverride);
            } else {
                groupOverride.groupColumnsOrder = groupColumnsOrder;
            }

            groupColumnsOrderOverrides.push(newGroupColumnsOrderOverride);
        }

        const updatedOverrides = { [visualizationId]: groupColumnsOrderOverrides };
        this.updateGridColumnOverrides(widgetOnBoardId, 'groupColumnsOrder', updatedOverrides, widgetPrefs?.currentVisualization)
            .pipe(first())
            .subscribe();
    }

    deleteGridColumnOverrides(widgetOnBoardId: number, columns: CustomColumnState[] = []): Observable<void> {
        return this.updateGridColumnOverrides(widgetOnBoardId)
            .pipe(
                first(),
                map(() => {
                    if (columns.length) {
                        this.restoreCheckboxColumn(columns, 'CustomColumnState');
                    }
                }),
            );
    }

    deleteOverridesForAllWidgetsOnBoard(dashboardId: string, allUsers = false, allClients = false): Observable<void> {
        let url = `dashboards/${dashboardId}/widgets/_/overrides`;
        if (allUsers) {
            url += '?allUsers=true';
        }
        if (allClients) {
            url += allUsers ? '&allClients=true' : '?allClients=true';
        }
        this.overrides = {};
        return this.ddvApiService.invokeService(this.clientCode, url, 'DELETE');
    }

    deleteWidgetOverrides(dashboardId: string, widgetId: string): Observable<void> {
        const url = `dashboards/${dashboardId}/widgets/${widgetId}/overrides`;
        this.overrides = {};
        return this.ddvApiService.invokeService(this.clientCode, url, 'DELETE');
    }

    setCheckboxColumnPosition(
        widgetOnBoardId: number,
        visualizationId: number,
        columns: ColDef[] | GridConfigItem[] | ColumnState[],
        columnType: string,
        isInViewMode: boolean,
        currentViz?: VisualizationType,
    ): void {
        const userGridColumnOverrides = this.getCurrentGridColumnOverrides(widgetOnBoardId, visualizationId, currentViz);
        // ConfigItem does have a field property so this line of code is bad
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const emptyColumnIndex = columns.findIndex((col: any) => !col?.colId && !col?.field);
        if (emptyColumnIndex >= 0) {
            columns.splice(emptyColumnIndex, 1);
        }
        const checkboxColumnIndex = columns.findIndex((col) => col.colId === crosstalkCheckboxFieldId);
        if (checkboxColumnIndex < 0) {
            this.addCheckboxColumn(userGridColumnOverrides, columns, columnType, isInViewMode);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const undefinedColumnIndex = columns.findIndex((col: any) => !col?.colId && !col?.field);
            if (undefinedColumnIndex >= 0) {
                columns.splice(undefinedColumnIndex, 1);
            }
        } else {
            if (isInViewMode) {
                const checkboxColumnOverridePosition = userGridColumnOverrides
                    .find((col) => col.columnId === crosstalkCheckboxFieldId)?.columnPosition;
                if (this.checkboxColumnDefinition) {
                    this.checkboxColumnDefinition.pinned = checkboxColumnOverridePosition ? null : 'left';
                }
                const checkboxColumn = this.getCheckboxColumnType(columnType);
                if (!checkboxColumnOverridePosition) {
                    columns.splice(checkboxColumnIndex, 1);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    columns.unshift(checkboxColumn as any);
                } else {
                    columns.splice(checkboxColumnIndex, 1);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    columns.splice(checkboxColumnOverridePosition, 0, checkboxColumn as any);
                }
            } else {
                this.restoreCheckboxColumn(columns, columnType);
            }
        }
    }

    moveAutoGroupColumnAfterCheckboxColumn(dataGridComponent: DataGridComponent | undefined, isInViewMode: boolean): void {
        const checkboxColumn = dataGridComponent?.getAllDisplayedColumns()?.find((col) => col.getColId() === crosstalkCheckboxFieldId);
        if (checkboxColumn && (checkboxColumn.getPinned() === 'left' || !isInViewMode)) {
            dataGridComponent?.moveColumn(crosstalkCheckboxFieldId, 0);
            dataGridComponent?.moveColumn(AUTO_GROUP_COLUMN_ID, 1);
        }
    }

    getColumnVisibility(
        overrides: UserGridColumnOverrides[],
        initialColumnState: CustomColumnState[],
        colId: string,
        isInViewMode: boolean,
    ): boolean {
        const override = overrides.find((c) => c.columnId === colId);
        const initialVisibility = !!initialColumnState.find((col) => col.colId === colId)?.hide;
        const isHidden = override?.isHidden != null && isInViewMode ? override.isHidden : initialVisibility;
        return !isHidden;
    }

    getColumnWidth(
        overrides: UserGridColumnOverrides[],
        colId: string,
        isInViewMode: boolean,
        initialColumnState: CustomColumnState[],
        autoSizeColumns: boolean,
    ): number | null | undefined {
        const override = overrides.find((o) => o.columnId === colId);
        const initialColumn = initialColumnState.find((c) => c.colId === colId);
        return isInViewMode && override?.columnWidth != null ?
            override.columnWidth :
            autoSizeColumns ? null : initialColumn?.width;
    }

    getGroupColumnWidth(
        overrides: UserGridColumnOverrides[],
        isInViewMode: boolean,
        initialColumnState: CustomColumnState[],
        groupColumns: Column[],
        autoSizeColumns: boolean,
    ): number | null | undefined {
        const groupOverride = overrides.find((o) => o.columnId === AUTO_GROUP_COLUMN_ID);
        const initialGroupColumnsOrder = groupColumns.map((c) => c.getColId()).join();
        const groupColumnsOrderSameAsInitial = groupOverride?.groupColumnsOrderForGroupWidth === initialGroupColumnsOrder;
        return isInViewMode && groupOverride?.columnWidth != null && groupColumnsOrderSameAsInitial ?
            groupOverride.columnWidth :
            autoSizeColumns ? null : initialColumnState.find((c) => c.colId === AUTO_GROUP_COLUMN_ID)?.width;
    }

    getGroupColumnsOrder(
        overrides: UserGridColumnOverrides[],
        isInViewMode: boolean,
        columns: GridConfigItem[],
        initialColumnState: CustomColumnState[],
    ): GridConfigItem[] {
        const updatedColumns = columns;
        const groupOverride = overrides.find((o) => o.columnId === AUTO_GROUP_COLUMN_ID);
        let groupColumnsOrder: { colId: string, rowGroupIndex: number | undefined | null }[];
        if (isInViewMode && groupOverride?.groupColumnsOrder != null) {
            if (groupOverride.groupColumnsOrder === '') {
                groupColumnsOrder = [];
            } else {
                groupColumnsOrder = groupOverride.groupColumnsOrder.split(',').map((colId, index) => ({
                    colId,
                    rowGroupIndex: index,
                }));
            }
        } else {
            groupColumnsOrder = initialColumnState
                .filter((c) => c.rowGroupIndex != null)
                .map((c) => ({ colId: c.colId, rowGroupIndex: c.rowGroupIndex }));
        }

        updatedColumns.forEach((c) => {
            const rowGroupIndex = groupColumnsOrder.find((o) => o.colId === c.colId)?.rowGroupIndex;
            c.rowGroupIndex = rowGroupIndex ?? null;
        });

        return updatedColumns;
    }

    // called only by the public method getCurrentGridColumnOverrides
    private getGridColumnOverrides(widgetOnBoardId: number): UserGridColumnOverridesMap {
        return this.overrides?.[widgetOnBoardId] ?? {};
    }

    private restoreCheckboxColumn(columns: ColDef[] | CustomColumnState[] | GridConfigItem[], columnType: string): void {
        const checkboxColumnIndex = columns.findIndex((col) => col.colId === crosstalkCheckboxFieldId);
        if (checkboxColumnIndex > -1) {
            if (this.checkboxColumnDefinition) {
                this.checkboxColumnDefinition.pinned = 'left';
            }

            columns.splice(checkboxColumnIndex, 1);
            const checkboxColumn = this.getCheckboxColumnType(columnType);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            columns.unshift(checkboxColumn as any);
        }
    }

    private addCheckboxColumn(
        userGridColumnOverrides: UserGridColumnOverrides[],
        columns: ColDef[] | GridConfigItem[] | ColumnState[],
        columnType: string,
        isInViewMode: boolean,
    ): void {
        const checkboxColumnPosition = userGridColumnOverrides.filter((col) => {
            return col.columnId === crosstalkCheckboxFieldId;
        })[0]?.columnPosition ?? -1;
        if (checkboxColumnPosition >= 0) {
            if (isInViewMode) {
                if (this.checkboxColumnDefinition) {
                    this.checkboxColumnDefinition.pinned = null;
                }
            } else {
                if (this.checkboxColumnDefinition) {
                    this.checkboxColumnDefinition.pinned = 'left';
                }
            }

            const checkboxColumn = this.getCheckboxColumnType(columnType);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            columns.splice(checkboxColumnPosition, 0, checkboxColumn as any);
        } else {
            if (this.checkboxColumnDefinition) {
                this.checkboxColumnDefinition.pinned = 'left';
            }
            const checkboxColumn = this.getCheckboxColumnType(columnType);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            columns.unshift(checkboxColumn as any);
        }
    }

    private updateGridColumnOverrides(
        widgetOnBoardId: number,
        propertyToUpdate = '',
        overrides?: UserGridColumnOverridesMap,
        currentViz?: VisualizationType,
    ): Observable<UserGridColumnOverridesObservable | unknown> {
        const dashboardId = this.manager.getCurrentDashboardId();
        const body = (!propertyToUpdate ? undefined : overrides) ?? null;

        return this.featureFlagService.isFlagEnabled('use-aws-for-visualization-overrides', this.clientCode).pipe(
            switchMap((isEnabled) => {
                if (isEnabled) {
                    if (dashboardId && overrides) {
                        const key = mapVisualizationTypeToOverrideVizType(currentViz);
                        const url = `dashboards/${dashboardId}/widgets/${widgetOnBoardId}/overrides`;
                        const combined = this.syncOverrides(widgetOnBoardId, overrides, key);
                        return this.ddvApiService.invokeServiceWithBody<UserGridColumnOverridesObservable>(
                            this.clientCode,
                            url,
                            'PUT',
                            { [key]: combined },
                        );
                    } else {
                        const url = `dashboards/${dashboardId}/widgets/${widgetOnBoardId}/overrides`;
                        return this.ddvApiService.invokeServiceWithBody(this.clientCode, url, 'DELETE', body);
                    }
                } else {
                    const url = `dashboards/${dashboardId}/widgets/${widgetOnBoardId}/userOverrides/${propertyToUpdate}`;
                    const method = !propertyToUpdate ? 'DELETE' : 'POST';
                    if (this.updateGridColumnOverrides$ && propertyToUpdate) {
                        this.updateGridColumnOverridesSubject.next({ url, method, body });
                        return this.updateGridColumnOverrides$;
                    }

                    return this.ddvMwService.invokeServiceWithBody(this.clientCode, url, method, body);
                }
            }),
        );
    }

    private syncOverrides(
        widgetOnBoardId: number,
        overrides: UserGridColumnOverridesMap,
        vizType: OverrideVizType,
    ): UserGridColumnOverrides[] {
        this.overrides = this.overrides || {};
        this.overrides[widgetOnBoardId] = this.overrides[widgetOnBoardId] || {};

        const newOverrides = Object.values(overrides)[0] as UserGridColumnOverrides[];
        const prevOverrides = this.overrides[widgetOnBoardId][vizType] ?? [];
        const syncedOverrides = newOverrides.map((override) => {
            const prevOverride = prevOverrides?.find((o: UserGridColumnOverrides) => o.columnId === override.columnId);
            return {
                ...(prevOverride ?? {}),
                ...override,
            };
        });

        prevOverrides.forEach((override) => {
            const isMissingCol = !syncedOverrides.find((o) => o.columnId === override.columnId);
            if (isMissingCol) {
                syncedOverrides.push(override);
            }
        });

        this.overrides[widgetOnBoardId][vizType] = syncedOverrides;
        return syncedOverrides;
    }

    private getConfigItemFromColDef(column: ColDef): GridConfigItem {
        return {
            colId: column.colId,
            resizable: false,
            width: column.width,
            pinned: column.pinned,
            minWidth: column.minWidth,
            maxWidth: column.maxWidth,
            columnCondition: [],
            agFilter: null,
        } as unknown as GridConfigItem;
    }

    private getCustomColumnStateFromColDef(column: ColDef): CustomColumnState {
        return {
            colId: column.colId ?? '',
            pinned: column.pinned,
            width: column.width,
            agFilter: null,
        };
    }

    private getCheckboxColumnType(columnType: string): ColDef | GridConfigItem | CustomColumnState | undefined {
        let checkboxColumn: ColDef | GridConfigItem | CustomColumnState | undefined;
        if (this.checkboxColumnDefinition) {
            if (columnType === 'ColDef') {
                checkboxColumn = this.checkboxColumnDefinition;
            } else if (columnType === 'ConfigItem') {
                checkboxColumn = this.getConfigItemFromColDef(this.checkboxColumnDefinition);
            } else if (columnType === 'CustomColumnState') {
                checkboxColumn = this.getCustomColumnStateFromColDef(this.checkboxColumnDefinition);
            }
        }

        return checkboxColumn;
    }

    private getColumnSort(
        sortOrder: UserGridColumnOverrides[],
        initialColumnState: CustomColumnState[],
        column: ColumnState,
    ): { agSort: { sort: AgGridSortDirection | undefined | null }, agSortIndex: number | undefined | null } {
        const override = sortOrder.find((c) => c.columnId === column.colId);
        const initialColumnSort = initialColumnState.find((c) => c.colId === column.colId);
        const agSort = override?.agSort != null ?
            { sort: override.agSort === 'none' ? null : override.agSort as AgGridSortDirection } :
            { sort: (initialColumnSort?.sort ?? null) as AgGridSortDirection };
        const agSortIndex = override?.agSort != null && override?.agSortIndex !== undefined ?
            override.agSortIndex :
            (initialColumnSort?.sortIndex !== undefined ? initialColumnSort.sortIndex : null);
        return { agSort, agSortIndex };
    }

    private getColumnFilters(
        isInViewMode: boolean,
        overrides: UserGridColumnOverrides[],
        initialColumnState: CustomColumnState[],
    ): AgGridFilterModel[] {
        const initialFilterState = initialColumnState.map((c) => {
            const filter = isInViewMode || !c.agFilterOnViewEditMode ?
                c.agFilter :
                Object.keys(c.agFilterOnViewEditMode).length ? c.agFilterOnViewEditMode : null;
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            return { [c.colId]: filter || null };
        });

        return isInViewMode && overrides.length ?
            overrides
                .sort((a, b) => a.agFilterIndex! > b.agFilterIndex! ? 1 : -1)
                .map((column) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    const initialFilter = initialFilterState.find((c) => c[column.columnId])?.[column.columnId] || null;
                    const overrideFilter = column.agFilter === 'default' || column.agFilter === '{}' || column.agFilter === undefined ?
                        initialFilter :
                        // eslint-disable-next-line eqeqeq
                        column.agFilter === null ? null : JSON.parse(column.agFilter);
                    return { [column.columnId]: overrideFilter };
                }) :
            initialFilterState;
    }

    private getColumnsFilterModel(
        overrides: UserGridColumnOverrides[],
        isInViewMode: boolean,
        initialColumnState: CustomColumnState[],
    ): AgGridFilterModel {
        const columnFilters = this.getColumnFilters(isInViewMode, overrides, initialColumnState);
        return this.getOverridesFilterModel(columnFilters);
    }

    private getOverridesFilterModel(columnFilters: AgGridFilterModel[]): AgGridFilterModel {
        const overrideFilterToApply: AgGridFilterModel = {};

        columnFilters.forEach((filterModel) => {
            const key = Object.keys(filterModel)[0];
            if (filterModel[key]) {
                let filter: AgGridFilterModel = {};

                if (filterModel[key].includedStrings) {
                    filter = getTextFilterModel(filterModel, key);
                } else if (filterModel[key].filterType === 'number') {
                    filter = getNumberFilterModel(filterModel, key);
                } else if (filterModel[key].filterType === 'date') {
                    filter = getDateFilterModel(filterModel, key);
                } else {
                    filter = filterModel;
                }

                Object.assign(overrideFilterToApply, filter);
            }
        });

        return overrideFilterToApply;
    }
}

export function getTextFilterModel(filterModel: AgGridFilterModel, key: string): AgGridFilterModel {
    return {
        [key]: {
            filterType: 'set',
            values: filterModel[key].values ?? JSON.parse(filterModel[key].includedStrings),
        },
    };
}

export function getNumberFilterModel(filterModel: AgGridFilterModel, key: string): AgGridFilterModel {
    if (filterModel[key].operator) {
        return {
            [key]: {
                filterType: 'number',
                operator: filterModel[key].operator,
                conditions: filterModel[key].conditions ??
                    [JSON.parse(filterModel[key].condition1), JSON.parse(filterModel[key].condition2)],
            },
        };
    }

    const filter = Number.isNaN(Number(filterModel[key].filter)) ?
        Number(filterModel[key].filterFrom) :
        Number(filterModel[key].filter);

    if (filterModel[key].filterTo) {
        return {
            [key]: {
                filterType: 'number',
                type: filterModel[key].type,
                filter,
                filterTo: filterModel[key].filterTo,
            },
        };
    }

    return {
        [key]: {
            filterType: 'number',
            type: filterModel[key].type,
            filter,
        },
    };
}

export function getDateFilterModel(filterModel: AgGridFilterModel, key: string): AgGridFilterModel {
    return {
        [key]: {
            filterType: 'date',
            type: filterModel[key].type,
            dateFrom: filterModel[key].dateFrom ?? filterModel[key].filterFrom,
            dateTo: filterModel[key].dateTo ?? filterModel[key].filterTo,
        },
    };
}

function mapVisualizationTypeToOverrideVizType(vizType?: VisualizationType): OverrideVizType {
    if (vizType === 'ADVANCED_GRID') {
        return 'advancedGrid';
    }
    if (vizType === 'SIMPLE_GRID') {
        return 'simpleGrid';
    }

    throw new Error(`Visualization overrides only work for advanced and simple grid, got: ${vizType}`);
}
